import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type {
	OnFetchParam,
	ProjectInput,
	ProjectPickerFilter,
	ProjectPickerPage,
	ProjectPickerState,
} from '../../../common/ui/types';
import messages from './messages';
import { ProjectPickerContent } from './project-picker-content';
import { SearchWithFilter } from './search-with-filter';

export type ProjectPickerProps = {
	// Lozenge value for "Existing projects" dropdown item in SearchWithFilter component
	currentCountForAssociatedProjects: number;
	filter: ProjectPickerFilter;
	onFetch: (onFetchParam: OnFetchParam) => void;
	onToggleAllProjects: (args0: boolean) => void;
	onToggleProject: (args0: ProjectInput) => void;
	/**
	 * Prop was originally used to display the count of "total projects"
	 * which was deemed confusing, this UI component has been disabled
	 * we should clean up this state and component when we are sure there
	 * is no need for it.
	 */
	newCountForAssociatedProjects: number;
	page: ProjectPickerPage;
	pendingChanges: Record<string, boolean>;
	priorityScheme: {
		name: string;
		isDefault: boolean;
	};
	selectAllChecked: boolean;
	state: (typeof ProjectPickerState)[keyof typeof ProjectPickerState];
};

export const ProjectPicker = ({
	currentCountForAssociatedProjects,
	filter,
	onFetch,
	onToggleAllProjects,
	onToggleProject,
	newCountForAssociatedProjects,
	page,
	pendingChanges,
	priorityScheme,
	selectAllChecked,
	state,
}: ProjectPickerProps) => {
	const { formatMessage } = useIntl();
	return (
		<Container>
			<TabPanelDescription>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p>{formatMessage(messages.projectsTabPanelInstruction)}</p>
				<p>{formatMessage(messages.projectsTabPanelDescription)}</p>
			</TabPanelDescription>
			<SearchWithFilter
				currentCountForAssociatedProjects={currentCountForAssociatedProjects}
				newCountForAssociatedProjects={newCountForAssociatedProjects}
				filter={filter}
				onFetch={onFetch}
			/>
			<ProjectPickerContent
				filter={filter}
				onFetch={onFetch}
				onInputChange={onToggleProject}
				onSelectAllInputChange={onToggleAllProjects}
				page={page}
				pendingChanges={pendingChanges}
				priorityScheme={priorityScheme}
				selectAllChecked={selectAllChecked}
				state={state}
			/>
		</Container>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{
	children?: ReactNode;
}>({
	width: '532px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TabPanelDescription = styled.div({
	marginTop: `${token('space.100', '8px')}`,
	marginBottom: `${token('space.200', '16px')}`,
});
